import cx from "classnames";
import { Button } from "components/Button";
import { ITEM_BRAND } from "config";
import { AppContext } from "context/app";
import { CartContext } from "context/cart";
import { useTranslationContext } from "context/translation";
import { getYotpoInstanceId } from "helpers/yotpo";
import { useCartAnalyticsEvents } from "hooks/analytics/useCartAnalyticsEvents";
import { useProductMachineAnalytics } from "hooks/analytics/useProductMachineAnalytics";
import { useRouter } from "next/router";
import {
  FC,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { Image, ResponsiveImageType } from "react-datocms/image";
import {
  BundleItemRecord,
  ProductBySlugQuery,
} from "services/datocms/generated";
import {
  ItemByGtinQuery,
  useDynamicBundlesQuery,
} from "services/graphql/generated";
import { isWhite } from "utils/isWhite";
import st from "./../ProductDetails.module.scss";
import { MachineImages } from "./MachineImages";
import { CustomDatoImage } from "components/CustomDatoImage/CustomDatoImage";
import { LocalizationContext } from "context/localization";
import Slider from "react-slick";
import { Spinner } from "components/spinner/Spinner";

interface ProductDetailsMachineProps {
  datoProduct: ProductBySlugQuery["product"] | null;
  pepDirectProduct: ItemByGtinQuery["itemByGtin"] | null;
  selectedImage: number;
  setSelectedImage: (index: number) => void;
  loading?: boolean;
}

export const ProductDetailsMachineV2: FC<ProductDetailsMachineProps> = ({
  datoProduct,
  selectedImage,
  setSelectedImage,
  pepDirectProduct,
  loading = false,
}) => {
  const router = useRouter();
  const [cartLoading, setCartLoading] = useState(false);
  const isColorSelected = (color: string) => {
    return router.query.id?.toString().toLowerCase() === color.toLowerCase();
  };
  const { formatPriceByLocale } = useContext(LocalizationContext);
  const { addToCart, comingSoon, chooseYourPack, selectYourColor } =
    useTranslationContext();
  const { tenant } = useContext(AppContext);
  const currency = tenant?.checkout?.paymentOptions?.defaultCurrencyCode;
  const { updateCartItemWithBundle } = useContext(CartContext);
  const { addToCartEvent } = useCartAnalyticsEvents();

  const { data: allDynamicBundles } = useDynamicBundlesQuery();
  useProductMachineAnalytics(
    currency,
    datoProduct,
    pepDirectProduct,
    allDynamicBundles,
  );

  useEffect(() => {
    if (datoProduct) {
      if (!router.query.variant) {
        router.replace(
          {
            query: {
              ...router.query,
              variant: datoProduct?.productBundles[0].bundleId,
            },
          },
          undefined,
          {
            shallow: true,
          },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datoProduct]);

  // We are using useLayoutEffect because we strictly need to make sure that the DOM element is attached so that Yotpo script can successfully attach and render the widget. With useEffect, there is no guarantee of having the element in the DOM even if we have the React state setup
  useLayoutEffect(() => {
    if (pepDirectProduct?.gtin) {
      (window as any)?.yotpoWidgetsContainer?.initWidgets();
    }
  }, [pepDirectProduct?.gtin]);

  const selectedBundleId =
    (router.query.variant as string) ||
    datoProduct?.productBundles[0].bundleId ||
    "";

  const colorOptions = useMemo(() => {
    const options = [
      {
        color: datoProduct?.color?.hex || "",
        slug: datoProduct?.slug || "",
        analyticsColor: datoProduct?.analyticsColorName || "",
      },
    ];

    datoProduct?.colorVariants.forEach((variant) => {
      variant?.color?.hex &&
        options.push({
          color: variant.color.hex,
          slug: variant?.slug || "",
          analyticsColor: variant?.analyticsColorName || "",
        });
    });

    return options.sort((option1, option2) =>
      option1.color > option2.color ? 1 : -1,
    );
  }, [datoProduct]);

  const handleAddBundleToCart = async (bundleTag: string) => {
    setCartLoading(true);
    const pepBundle = allDynamicBundles?.dynamicBundles?.find(
      (bundle) => bundle?.tag === selectedBundleId,
    );

    const item = {
      itemId: pepBundle?.id,
      gtin: pepBundle?.primaryComponent.items?.[0]?.gtin,
      itemName: pepBundle?.name,
      itemBrand: ITEM_BRAND,
      itemCategory: datoProduct?.category?.analyticsCategoryName,
      price: pepBundle?.discountPrice ? pepBundle?.discountPrice / 100 : 0,
      itemListName: undefined,
      currency,
      quantity: 1,
      index:
        (datoProduct?.productBundles.findIndex(
          (bundle) => bundle.bundleId === selectedBundleId,
        ) ?? 0) + 1,
    };

    const ecommerce = {
      currency,
      itemListName: undefined,
      items: [item],
      value: pepBundle?.discountPrice ? pepBundle?.discountPrice / 100 : 0,
    };
    const newCart = await updateCartItemWithBundle({
      bundleTag,
      quantity: 1,
      analyticsDetails: ecommerce,
    });
    await addToCartEvent(newCart, ecommerce);
    router.push("/cart");

    const handleRouteChangeComplete = () => {
      setCartLoading(false);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
    router.events.on("routeChangeComplete", handleRouteChangeComplete);
  };

  useEffect(() => {
    setSelectedImage(0);
  }, [router.query, setSelectedImage]);

  const selectedPepBundle = allDynamicBundles?.dynamicBundles?.find(
    (bundle) => bundle?.tag === selectedBundleId,
  );
  const selectedDatoBundle = datoProduct?.productBundles.find(
    (bundle) => bundle.bundleId === selectedBundleId,
  );

  const outOfStock =
    !loading && selectedPepBundle && selectedPepBundle.inventoryQty === 0;

  const addToCartButtonText = loading
    ? ""
    : outOfStock
      ? comingSoon
      : addToCart;

  const onBundleSelect = (bundleId?: string | null) => {
    if (!bundleId) {
      return;
    }
    router.replace(
      {
        query: {
          ...router.query,
          variant: bundleId,
        },
      },
      undefined,
      {
        shallow: true,
        scroll: false,
      },
    );
  };

  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    className: "product-bundle-slider",
    afterChange: (currentSlide: number) => {
      const bundle = datoProduct?.productBundles[currentSlide];
      if (bundle) {
        onBundleSelect(bundle.bundleId);
      }
    },
    centerMode: true,
    centerPadding: "30px",
    adaptiveHeight: true,
  };

  return (
    <>
      <div className="bg-machine-pdp">
        <div className="absolute top-[150px] left-0 w-full h-[250px] overflow-hidden pointer-events-none">
          <span
            className="absolute text-[150px] md:text-[300px] font-bold text-[#75A7AD] opacity-10 whitespace-nowrap select-none"
            style={{ top: "50%", left: "0", transform: "translate(10%, -50%)" }}
          >
            {datoProduct?.title}
          </span>
        </div>

        <div className="md:mx-[0px] flex flex-col lg:flex-row justify-center items-center pt-[18px] md:pt-[28px] md:mb-[24px] relative">
          <div className="container flex flex-col lg:flex-row gap-2 items-center lg:items-start !px-0 md:!px-0">
            <div className="flex flex-col lg:w-3/5 md:pr-8 justify-between md:pb-[24px] px-6">
              <div className="block lg:hidden mb-5">
                <h1 className="text-[#002238] font-[Poppins] text-[34px] leading-[20px] md:text-[50px] md:leading-[65px] pb-[15px] font-semibold">
                  {datoProduct?.title}
                </h1>
                <span className="text-[#4E4E4E] font-[Poppins] text-[14px] font-medium mb-[7px]">
                  {datoProduct?.shortDescription}
                </span>
              </div>

              <MachineImages
                secondaryContainerClassName="!bg-transparent"
                datoProduct={datoProduct}
                selectedImage={selectedImage}
                bundleImage={selectedDatoBundle?.bundleImage?.responsiveImage}
                setSelectedImage={setSelectedImage}
                title={pepDirectProduct?.title ?? ""}
              />

              {selectedDatoBundle?.description?.header && (
                <div className="mt-5 hidden lg:block">
                  <h3 className="text-[#002238] font-[Poppins] text-[18px] font-[600] mb-1">
                    {selectedDatoBundle?.description?.header}
                  </h3>
                  <p className="text-[#002238] font-[Poppins] text-[12px] font-[400] mb-5">
                    {selectedDatoBundle?.description?.text}
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse lg:flex-col lg:w-3/5 h-full">
              <>
                <div
                  className={cx(
                    st.productBundlesContainer,
                    `flex flex-col overflow-y-scroll p-0 md:p-[15px] `,
                  )}
                >
                  <div className="hidden lg:block">
                    <h1 className="text-[#002238] font-[Poppins] text-[34px] leading-[20px] md:text-[50px] md:leading-[65px] pb-[15px] font-semibold">
                      {datoProduct?.title}
                    </h1>
                    <span className="text-[#4E4E4E] font-[Poppins] text-[14px] font-medium mb-[7px]">
                      {datoProduct?.shortDescription}
                    </span>
                  </div>

                  {pepDirectProduct?.gtin && (
                    <div
                      className="yotpo-widget-instance"
                      data-yotpo-instance-id={getYotpoInstanceId("stars")}
                      data-yotpo-product-id={pepDirectProduct?.gtin}
                    />
                  )}

                  <div className="flex flex-col items-start whitespace-nowrap w-full gap-2 md:justify-center mt-5 mb-5 px-[15px] md:px-0">
                    <span className={st.colorTitle}>{selectYourColor}</span>
                    <div className={cx(st.colorOptionsContainer, "!pl-[7px]")}>
                      {colorOptions?.map((option, index) => (
                        <div
                          key={`${index}-${option.slug}`}
                          className={st.colorLink}
                          onClick={() => {
                            if (isColorSelected(option.slug)) return;
                            router.push(
                              `/${datoProduct?.category?.slug}/${option.slug}?exp=v2`,
                            );
                          }}
                          bi-type={`${pepDirectProduct?.title} pdp - Color selection`}
                          bi-id={option.analyticsColor}
                        >
                          {isColorSelected(option.slug) && (
                            <span
                              className={st.selectedColor}
                              style={{
                                borderColor: isWhite(option.color)
                                  ? "#8B9195"
                                  : option.color,
                              }}
                            />
                          )}
                          <div
                            className={st.colorOption}
                            style={{
                              backgroundColor: option.color,
                              border: isWhite(option.color)
                                ? "0.65px solid #8B9195"
                                : "none",
                              width: "25px",
                              height: "25px",
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <span className=" text-[#002238] font-[Poppins] text-[14px] font-[600] font-feature-settings-['clig'_0,'liga'_0] mb-5 px-[15px] md:px-0">
                    {chooseYourPack}
                  </span>
                  <div className="">
                    <div className="hidden md:flex mb-0">
                      {datoProduct?.productBundles?.map((bundle, index) => {
                        const pepBundle =
                          allDynamicBundles?.dynamicBundles?.find(
                            (item) => item?.tag === bundle.bundleId,
                          );
                        const isSelected = bundle.bundleId === selectedBundleId;

                        return (
                          <button
                            key={bundle.bundleId}
                            onClick={() => onBundleSelect(bundle.bundleId)}
                            className={`flex flex-col items-center px-2 md:px-4 py-2 relative transition-all duration-200 basis-0 flex-grow ${
                              isSelected
                                ? "bg-white rounded-tr-lg rounded-tl-lg rounded-b-none text-[#002238] border-r-2 border-t-2 border-l-2 border-[#f0f0f0] -translate-y-3 before:absolute before:bottom-[-12px] before:left-[-2px] before:w-[calc(100%+4px)] before:h-3 before:bg-white before:border-r-2 before:border-l-2 before:border-[#f0f0f0]"
                                : "bg-[#EEEAE5] text-[#002238] hover:bg-[#D5D1CC]"
                            } ${
                              index === 0
                                ? "rounded-tl-lg"
                                : index ===
                                    datoProduct.productBundles.length - 1
                                  ? "rounded-tr-lg"
                                  : ""
                            }`}
                          >
                            <span className="font-[Poppins] text-[12px] md:text-[14px] font-medium mb-1 break-words">
                              {bundle.title}
                            </span>
                            <span className="font-[Poppins] text-[14px] md:text-[16px] font-semibold">
                              {formatPriceByLocale(pepBundle?.discountPrice)}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="md:hidden w-full">
                      <div className="w-full">
                        <Slider {...sliderSettings}>
                          {datoProduct?.productBundles?.map((bundle, index) => {
                            const pepBundle =
                              allDynamicBundles?.dynamicBundles?.find(
                                (item) => item?.tag === bundle.bundleId,
                              );
                            const isSelected =
                              bundle.bundleId === selectedBundleId;

                            return (
                              <div
                                key={bundle.bundleId}
                                className="w-full border-[1px] border-[#CCCCCC]"
                              >
                                <div className="w-full">
                                  <div
                                    className={`w-full flex flex-col items-start border-b-[1px] border-[#CCCCCC] p-2 ${
                                      isSelected ? "bg-white" : "bg-[#EEEAE5]"
                                    }`}
                                  >
                                    <span className="font-[Poppins] text-[14px] text-start font-medium mb-0">
                                      {bundle.title}
                                    </span>
                                    <span className="font-[Poppins] text-[16px] font-semibold">
                                      {formatPriceByLocale(
                                        pepBundle?.discountPrice,
                                      )}
                                    </span>
                                  </div>

                                  <div className="bg-white rounded-lg p-4 mt-2">
                                    <div className="grid grid-cols-1 gap-2">
                                      {(
                                        bundle.bundleItems as BundleItemRecord[]
                                      ).map((bundleItem, idx) => (
                                        <div
                                          key={idx}
                                          className="flex items-center gap-2"
                                        >
                                          <div className="h-[28px] w-[28px] bg-[#F4F6F7] rounded-full flex items-center justify-center">
                                            <span className="text-[#002238] font-[Poppins] text-[14px] font-medium">
                                              {bundleItem.amount}x
                                            </span>
                                          </div>
                                          <span className="text-[#002238] font-[Poppins] text-[14px]">
                                            {bundleItem.title}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </Slider>
                      </div>
                    </div>

                    <div className="hidden md:block bg-white border-r-2 border-b-2 border-l-2 border-[#f0f0f0] rounded-b-lg p-6">
                      {selectedDatoBundle && (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            {(
                              selectedDatoBundle.bundleItems as BundleItemRecord[]
                            ).map((bundleItem, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2"
                              >
                                <div className="h-[28px] w-[28px] bg-[#F4F6F7] rounded-full flex items-center justify-center">
                                  <span className="text-[#002238] font-[Poppins] text-[14px] font-medium">
                                    {bundleItem.amount}x
                                  </span>
                                </div>
                                <span className="text-[#002238] font-[Poppins] text-[14px]">
                                  {bundleItem.title}
                                </span>
                              </div>
                            ))}
                          </div>
                          {selectedDatoBundle.lowestPriceText && (
                            <div className={st.lowestPriceText}>
                              {selectedDatoBundle.lowestPriceText}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="px-[15px] md:px-0">
                    <Button
                      className="flex flex-col h-full py-[10px] w-full min-h-[44px] mt-4"
                      onClick={() => handleAddBundleToCart(selectedBundleId)}
                      disabled={outOfStock || loading || cartLoading}
                      bi-type={`${pepDirectProduct?.title} pdp - Add to cart`}
                      bi-id={
                        allDynamicBundles?.dynamicBundles?.find(
                          (bundle) => bundle?.tag === selectedBundleId,
                        )?.name
                      }
                    >
                      <span>
                        {cartLoading ? (
                          <Spinner color="white" dotSize="10px" />
                        ) : (
                          addToCartButtonText
                        )}
                      </span>
                    </Button>
                  </div>

                  <div className="flex gap-2 mt-6 px-[15px] md:px-0">
                    {datoProduct?.productBenefits?.map((benefit, index) => (
                      <div
                        key={index}
                        className="flex flex-col items-center gap-4"
                      >
                        <CustomDatoImage
                          width={80}
                          data={
                            benefit.image
                              ?.responsiveImage as ResponsiveImageType
                          }
                        />
                        <span className="text-[#002238] font-[Poppins] text-[14px] font-medium text-center">
                          {benefit.text}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="lg:hidden mt-6 px-[15px] md:px-0">
                    {selectedDatoBundle?.description?.header && (
                      <>
                        <h3 className="text-[#002238] font-[Poppins] text-[18px] font-[600] mb-1">
                          {selectedDatoBundle?.description?.header}
                        </h3>
                        <p className="text-[#002238] font-[Poppins] text-[12px] font-[400] mb-5">
                          {selectedDatoBundle?.description?.text}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#F4F2EE] flex flex-col-reverse md:flex-row items-center justify-center w-full rounded-[60px]">
        <div className="container">
          <div className="2xl:px-[220px] flex flex-col-reverse md:flex-row items-center md:justify-between ">
            <div className="md:w-1/2 lg:w-3/5 text-left pt-[80px] pb-[80px] flex flex-col gap-[24px]">
              <span className="text-[#005D6E] font-[Poppins] text-[56px] font-[600] leading-[57px]">
                {datoProduct?.heroTitle}
              </span>
              <p className="text-[#005D6E] font-[Poppins] text-[14px] font-[400] leading-[20px]">
                {datoProduct?.heroDescription}
              </p>
            </div>
            <div className="md:w-1/2 lg:w-2/5 md:h-[400px] lg:h-[500px] flex justify-center items-center">
              {datoProduct?.heroImage?.responsiveImage && (
                <Image data={datoProduct.heroImage.responsiveImage} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
